import { Injectable } from '@angular/core';
import { CarrierDeliveryNoteBundleOverview } from '../../../api/models/carrier-delivery-note-bundle-overview';
import { CarrierDeliveryNoteInPool } from '../../../api/models/carrier-delivery-note-in-pool';
import { lastValueFrom, ReplaySubject, Subject, takeUntil, EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserRoles } from '../../../api/models/user-roles';
import { LoginOrganizationSite } from '../../../api/models/login-organization-site';
import { DeliveryNoteBundlesService } from '../../../api/services/delivery-note-bundles.service';
import { DeliveryNotesService } from '../../../api/services/delivery-notes.service';
import { TourResponse } from '../../../api/models/tour-response';
import { ToursService } from '../../../api/services/tours.service';

export interface DeliveryNoteBundleWithOrgSiteKey
    extends CarrierDeliveryNoteBundleOverview {
    orgSiteKey: string;
    read_only: boolean;
}

export interface DeliveryNoteWithOrgSiteKey extends CarrierDeliveryNoteInPool {
    orgSiteKey: string;
    read_only: boolean;
}

export interface TourWithOrgSiteKey extends TourResponse {
    orgSiteKey: string;
    read_only: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class CarrierService {
    /**
     * Bundles
     */
    public deliveryNoteBundlesReplaySubject: ReplaySubject<
        DeliveryNoteBundleWithOrgSiteKey[]
    > = new ReplaySubject<DeliveryNoteBundleWithOrgSiteKey[]>(1);
    private deliveryNoteBundles: DeliveryNoteBundleWithOrgSiteKey[] = [];

    /**
     * Tours
     */
    public toursReplaySubject: ReplaySubject<TourWithOrgSiteKey[]> =
        new ReplaySubject<TourWithOrgSiteKey[]>(1);
    private tours: TourWithOrgSiteKey[] = [];

    /**
     * Delivery notes
     */
    public deliveryNotesReplaySubject: ReplaySubject<
        DeliveryNoteWithOrgSiteKey[]
    > = new ReplaySubject<DeliveryNoteWithOrgSiteKey[]>(1);
    private deliveryNotes: DeliveryNoteWithOrgSiteKey[] = [];

    private destroy$ = new Subject<void>();

    constructor(
        private deliveryNoteBundlesService: DeliveryNoteBundlesService,
        private deliveryNotesService: DeliveryNotesService,
        private toursService: ToursService
    ) {}

    /**
     * load bundles
     */
    async loadDeliveryNoteBundleOverviewsForSite(
        site: LoginOrganizationSite
    ): Promise<void> {
        console.log('loading bundles');
        this.deliveryNoteBundles = [];

        try {
            const bundleOverviews = await lastValueFrom(
                this.deliveryNoteBundlesService
                    .getCarrierBundles({
                        organizationSiteKey: site._key,
                    })
                    .pipe(
                        takeUntil(this.destroy$),
                        catchError(() => {
                            // Silently handle cancellation
                            return EMPTY;
                        })
                    ),
                { defaultValue: [] }
            );

            bundleOverviews.forEach(overview => {
                this.deliveryNoteBundles.push({
                    orgSiteKey: site._key,
                    read_only:
                        site.roles.includes(UserRoles.RemoteCarrier) &&
                        !site.roles.includes(UserRoles.Carrier),
                    ...overview,
                });
            });
            this.deliveryNoteBundlesReplaySubject.next(
                this.deliveryNoteBundles
            );
        } catch (error: any) {
            // Ignore errors from cancelled requests
            if (error?.name !== 'EmptyError') {
                throw error;
            }
        }
    }

    castDeliveryNoteBundleOverviews(
        bundles: CarrierDeliveryNoteBundleOverview[],
        site: LoginOrganizationSite
    ): DeliveryNoteBundleWithOrgSiteKey[] {
        if (bundles && site) {
            let bundlesWithOrgSiteKey: DeliveryNoteBundleWithOrgSiteKey[] = [];
            for (const bundle of bundles) {
                bundlesWithOrgSiteKey.push({
                    orgSiteKey: site._key,
                    read_only:
                        site.roles.includes(UserRoles.RemoteCarrier) &&
                        !site.roles.includes(UserRoles.Carrier),
                    ...bundle,
                });
            }

            return bundlesWithOrgSiteKey;
        }
        return [];
    }

    /**
     * load tours
     */
    async loadToursForSite(site: LoginOrganizationSite): Promise<void> {
        this.tours = [];

        try {
            const tours = await lastValueFrom(
                this.toursService
                    .getTours({
                        organizationSiteKey: site._key,
                    })
                    .pipe(
                        takeUntil(this.destroy$),
                        catchError(() => {
                            // Silently handle cancellation
                            return EMPTY;
                        })
                    ),
                { defaultValue: [] }
            );

            tours.forEach(tour => {
                this.tours.push({
                    orgSiteKey: site._key,
                    read_only:
                        site.roles.includes(UserRoles.RemoteCarrier) &&
                        !site.roles.includes(UserRoles.Carrier),
                    ...tour,
                });
            });
            this.toursReplaySubject.next(this.tours);
        } catch (error: any) {
            // Ignore errors from cancelled requests
            if (error?.name !== 'EmptyError') {
                throw error;
            }
        }
    }

    /**
     * load delivery notes
     */
    async loadDeliveryNotesForSite(site: LoginOrganizationSite): Promise<void> {
        this.deliveryNotes = [];

        try {
            const notes = await lastValueFrom(
                this.deliveryNotesService
                    .getCarrierDeliveryNotesMetadataInPool({
                        organizationSiteKey: site._key,
                    })
                    .pipe(
                        takeUntil(this.destroy$),
                        catchError(() => {
                            // Silently handle cancellation
                            return EMPTY;
                        })
                    ),
                { defaultValue: [] }
            );

            notes.forEach(note => {
                this.deliveryNotes.push({
                    orgSiteKey: site._key,
                    read_only:
                        site.roles.includes(UserRoles.RemoteCarrier) &&
                        !site.roles.includes(UserRoles.Carrier),
                    ...note,
                });
            });
            this.deliveryNotesReplaySubject.next(this.deliveryNotes);
        } catch (error: any) {
            // Ignore errors from cancelled requests
            if (error?.name !== 'EmptyError') {
                throw error;
            }
        }
    }

    castDeliveryNotes(
        notes: CarrierDeliveryNoteInPool[],
        site: LoginOrganizationSite
    ): DeliveryNoteWithOrgSiteKey[] {
        if (notes && site) {
            let notesWithOrgSiteKey: DeliveryNoteWithOrgSiteKey[] = [];
            for (const note of notes) {
                notesWithOrgSiteKey.push({
                    orgSiteKey: site._key,
                    read_only:
                        site.roles.includes(UserRoles.RemoteCarrier) &&
                        !site.roles.includes(UserRoles.Carrier),
                    ...note,
                });
            }

            return notesWithOrgSiteKey;
        }
        return [];
    }

    /**
     * Cancel all active HTTP requests
     */
    cancelActiveRequests(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.destroy$ = new Subject<void>();
    }
}
